import React, { useEffect, useState } from "react";
import { IoArrowBackSharp } from "react-icons/io5";
import { FaExternalLinkAlt } from "react-icons/fa";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Button } from "react-bootstrap";
import styles from "./MyAppsDetail.module.css";
import { useNavigate } from "react-router-dom";
import ToggleSwitch from "./ToggleSwitch";
import toggleStyles from "./toggle.module.css";
import { convertDateFromTFormat } from "../../store/Currency_Converters/CurrencyConverter.ts";
import InfoWithPopover from "../../store/InfoWithPopover/InfoWithPopover";
import AdminUpdateSubscriptionDrawer from "./AdminUpdateSubscriptionDrawer";
import VendorPriceEditor from "./VendorPriceEditor";
import AddSavingsModal from "./AddSavingsModal";
import { openAdminSalesOrder } from "../../utils/salesOrderPdf";

// openAdminSalesOrder lives in utils/salesOrderPdf — this file used to carry
// its own copy, so the popup-blocker and silent-failure fixes had to be made
// twice and were not. One implementation, used here and by DomainDetail.

const REACT_APP_SERVER_URL = process.env.REACT_APP_SERVER_URL;
const MyAppDetail = ({
  selectedApp,
  setSelectedApp,
  isSomethingChanged,
  setIsSomethingChanged,
}) => {
  const navigate = useNavigate();
  const [pastStripeInvoice, setPastStripeInvoice] = useState(null);
  // Automatic licence add/remove for this subscription. Off means the
  // sweeps ignore it — used where the vendor's user count and the paid
  // licence count legitimately differ (any non-Flex reseller plan).
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [autoSyncOn, setAutoSyncOn] = useState(null);

  // Effective value: local override once toggled, else whatever the row holds.
  // MySQL hands a BOOLEAN back as 0/1, so `!== false` would read 0 as ENABLED
  // and show the toggle on for a subscription whose sync is off. Compare the
  // off-values explicitly; anything else (including undefined on an older row)
  // means on, which is the column default.
  const autoSyncOff =
    selectedApp?.auto_license_sync === false ||
    selectedApp?.auto_license_sync === 0 ||
    selectedApp?.auto_license_sync === "0" ||
    selectedApp?.auto_license_sync === "false";
  const autoSyncEnabled = autoSyncOn !== null ? autoSyncOn : !autoSyncOff;

  const toggleAutoLicenseSync = async (next) => {
    if (!selectedApp?.id) return;
    setAutoSyncSaving(true);
    try {
      const res = await fetch(
        `${REACT_APP_SERVER_URL}/admin/subscriptions/${selectedApp.id}/auto_license_sync`,
        {
          headers: { Accept: "application/json", "Content-Type": "application/json" },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ enabled: next }),
        }
      );
      const data = await res.json();
      if (data?.status === 200) {
        setAutoSyncOn(next);
        setIsSomethingChanged(!isSomethingChanged);
      } else {
        setErrorMessage(data?.msg || "Could not update licence sync");
        setErrorSnackbarOpen(true);
      }
    } catch (error) {
      console.log("auto_license_sync failed:", error);
      setErrorMessage("Could not update licence sync");
      setErrorSnackbarOpen(true);
    } finally {
      setAutoSyncSaving(false);
    }
  };
  const [pastInvoices, setPastInvoices] = useState(null);
  const [pastOrders, setPastOrders] = useState(null);
  const [pastBills, setPastBills] = useState(null);
  const [pastPayments, setPastPayments] = useState(null);
  const [payConfirmation, setPayConfirmation] = useState(false);
  const [payId, setPayId] = useState({});
  const [voidConfirmation, setVoidConfirmation] = useState(false);
  const [voidId, setVoidId] = useState({});
  const [voidLoading, setVoidLoading] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [errorSnackbarOpen, setErrorSnackbarOpen] = React.useState(null);

  // The sales-order buttons used to swallow every failure, so a broken Zoho
  // call and a dead button were indistinguishable. Route both into the
  // snackbar this page already has.
  const showSalesOrderError = React.useCallback((msg) => {
    setErrorMessage(msg || "Could not open the sales order.");
    setErrorSnackbarOpen(true);
  }, []);
  const [showUpdateDrawer, setShowUpdateDrawer] = useState(false);
  const [showAddSavings, setShowAddSavings] = useState(false);
  const [verifyLoader, setVerifyLoader] = useState(false);

  const canUpdateSubscription = Boolean(
    selectedApp &&
      String(selectedApp?.type || "").toLowerCase() === "recurring" &&
      !["cancelled", "canceled"].includes(
        String(selectedApp?.status || "").toLowerCase(),
      ),
  );

  // A cancelled sub cannot be updated (no Stripe object left to patch),
  // but its bills keep amortising into the Revenue Tracker for up to 12
  // months after cancellation — so its vendor cost still has to be
  // editable, or those months stay wrong forever. The vendor_price edit
  // is purely local (no Stripe call), so it is safe on a dead sub.
  const showStandaloneVendorEditor = Boolean(
    selectedApp && !canUpdateSubscription,
  );

  useEffect(() => {
    if (selectedApp) {
      const fetchPastPayments = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_payments`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: selectedApp.stripe_sub }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastPayments(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastBills = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_bills`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: selectedApp.stripe_sub }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastBills(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastInvoices = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_invoices`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: selectedApp.stripe_sub }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastInvoices(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastOrders = async () => {
        return fetch(`${process.env.REACT_APP_SERVER_URL}/past_orders`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "POST",
          body: JSON.stringify({ stripe_sub: selectedApp.stripe_sub }),
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastOrders(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };
      const fetchPastStripeInvoices = async () => {
        return fetch(
          `${process.env.REACT_APP_SERVER_URL}/past_stripe_invoices`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            credentials: "include",
            method: "POST",
            body: JSON.stringify({
              stripe_sub: selectedApp.stripe_sub,
              regionId: selectedApp.regionId,
            }),
          },
        )
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else if (response.status === 201) {
              console.log("DATA NOT FETCHED");
            } else if (response.status === 202) {
              navigate("/login");
            }
          })
          .then((data) => {
            setPastStripeInvoice(data.data);
          })
          .catch((err) => {
            console.log(err);
          });
      };

      const fetchAllData = async () => {
        try {
          await fetchPastPayments();
          await fetchPastBills();
          await fetchPastInvoices();
          await fetchPastOrders();
          await fetchPastStripeInvoices();
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };

      fetchAllData();
    }
    // eslint-disable-next-line
  }, [selectedApp]);

  const handleErrorSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setErrorSnackbarOpen(false);
  };

  const payInvoice = () => {
    setVerifyLoader(true);
    fetch(`${REACT_APP_SERVER_URL}/pay_invoice`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        invoice_id: payId.invoice_id,
        regionId: payId.region,
      }),
    })
      .then((res) => {
        if (res.ok) {
          return res.json();
        }
      })
      .then((data) => {
        if (data.status === 200) {
          setPayConfirmation(false);
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data.msg);
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setVerifyLoader(false);
      });
  };

  // Accept a customer's manually-submitted payment → marks the Stripe invoice
  // paid_out_of_band (same as Pay) and clears the claim.
  const acceptManualPayment = (item) => {
    setVerifyLoader(true);
    fetch(`${REACT_APP_SERVER_URL}/accept_manual_payment`, {
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({
        bill_id: item?._bill?.bill_id,
        invoice_id: item.id,
        regionId: selectedApp.regionId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200) {
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data.msg || "Could not accept payment");
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setVerifyLoader(false));
  };

  // Reject a manual claim → bill returns to overdue so the customer can re-pay.
  const rejectManualPayment = (item) => {
    setVerifyLoader(true);
    fetch(`${REACT_APP_SERVER_URL}/reject_manual_payment`, {
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      method: "POST",
      body: JSON.stringify({ bill_id: item?._bill?.bill_id }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 200) {
          setIsSomethingChanged(!isSomethingChanged);
        } else {
          setErrorMessage(data.msg || "Could not reject payment");
          setErrorSnackbarOpen(true);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setVerifyLoader(false));
  };

  // Mark a Stripe invoice as void via the regional Stripe client. The
  // bills row gets flipped to status="void" by the invoice.voided
  // webhook in app.easexpense.node so we don't update local state here
  // beyond bumping isSomethingChanged to trigger a refetch.
  const voidInvoice = async () => {
    if (!voidId.invoice_id) {
      setErrorMessage("No Stripe invoice ID — nothing to void");
      setErrorSnackbarOpen(true);
      setVoidConfirmation(false);
      return;
    }
    setVoidLoading(true);
    try {
      const res = await fetch(`${REACT_APP_SERVER_URL}/void_invoice`, {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
        method: "POST",
        body: JSON.stringify({
          invoice_id: voidId.invoice_id,
          regionId: voidId.region,
        }),
      });
      const data = await res.json();
      if (data?.status === 200) {
        setVoidConfirmation(false);
        setIsSomethingChanged(!isSomethingChanged);
      } else {
        setErrorMessage(data?.msg || "Void failed");
        setErrorSnackbarOpen(true);
      }
    } catch (error) {
      console.log("void_invoice failed:", error);
      setErrorMessage("Void failed");
      setErrorSnackbarOpen(true);
    } finally {
      setVoidLoading(false);
    }
  };

  const handleSubscriptionUpdated = () => {
    setShowUpdateDrawer(false);
    setIsSomethingChanged(!isSomethingChanged);
  };

  const isIndiaSubscription = Boolean(
    selectedApp &&
      (String(selectedApp?.currency || "").toUpperCase() === "INR" ||
        String(selectedApp?.region || "").toUpperCase() === "IN" ||
        String(selectedApp?.country_code || "").toUpperCase() === "IN"),
  );
  const previewTaxPercent = 18;
  const previewBaseAmount = Number(selectedApp?.amount || 0);
  const previewTaxAmount = Number(
    (previewBaseAmount * (previewTaxPercent / 100)).toFixed(2),
  );
  const previewTotalIncludingTax = Number(
    (previewBaseAmount + previewTaxAmount).toFixed(2),
  );

  return (
    <>
      <Dialog
        PaperProps={{
          style: { borderRadius: "20px" },
        }}
        open={payConfirmation}
      >
        <div
          style={{
            width: "500px",
            height: "200px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => {
                if (!verifyLoader) setPayConfirmation(false);
              }}
            />
          </div>
          <DialogTitle id="alert-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Sure you want to mark as paid!! (Try to mark only if payment is
              recived)
            </p>
          </DialogTitle>
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setPayConfirmation(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
              disabled={verifyLoader}
            >
              Cancel
            </Button>
            <Button
              onClick={() => payInvoice()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="success"
              disabled={verifyLoader}
            >
              {verifyLoader ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  Verifying...
                </span>
              ) : (
                "Verify"
              )}
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      <Dialog
        PaperProps={{ style: { borderRadius: "20px" } }}
        open={voidConfirmation}
      >
        <div
          style={{
            width: "500px",
            minHeight: "220px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "0 20px",
          }}
        >
          <div style={{ width: "100%" }}>
            <CloseIcon
              sx={{
                float: "right",
                color: "black",
                height: "4vh",
                mx: 2,
                mt: 3,
                cursor: "pointer",
              }}
              onClick={() => {
                if (!voidLoading) setVoidConfirmation(false);
              }}
            />
          </div>
          <DialogTitle id="void-dialog-title" sx={{ mt: 0, pt: 0 }}>
            <p
              style={{ color: "#EF6D6D", fontWeight: "bold", fontSize: "20px" }}
            >
              Void this Stripe invoice?
            </p>
            <p style={{ color: "#555", fontSize: "14px", marginTop: "8px" }}>
              Calls Stripe to void the invoice. The bill row flips to
              status=void when Stripe's invoice.voided webhook fires.
              Cannot be undone.
            </p>
          </DialogTitle>
          <DialogActions sx={{ mb: 4, float: "right" }}>
            <Button
              onClick={() => setVoidConfirmation(false)}
              variant="contained"
              sx={{ color: "#fff" }}
              color="inherit"
              disabled={voidLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => voidInvoice()}
              variant="contained"
              sx={{ color: "#fff" }}
              color="error"
              disabled={voidLoading}
            >
              {voidLoading ? (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                  Voiding...
                </span>
              ) : (
                "Confirm void"
              )}
            </Button>
          </DialogActions>
        </div>
      </Dialog>
      {Object.keys(selectedApp).length !== 0 && (
        <div className={styles.mainContainer}>
          <div className={styles.headingContainer}>
            <div className={styles.headingLeftContainer}>
              <IoArrowBackSharp
                onClick={() => {
                  setSelectedApp([]);
                }}
                className={styles.backButton}
              />
              <h3>{selectedApp.marketplaceName}</h3>
              <div
                style={{
                  backgroundColor: "#4CAF50",
                  color: "white",
                  fontFamily: "Poppins",
                  fontSize: "0.7rem",
                  padding: "2px 6px",
                  borderRadius: "6px",
                  border: "#4CAF50",
                  marginTop: "0.5rem",
                  marginLeft: "1.5rem",
                  marginBottom: "0.5rem",
                  transition:
                    "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                  display: "inline-block",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                {selectedApp.status}
              </div>
              <ToggleSwitch
                nextOnlinePayment={selectedApp.onlinePayment}
                api={"online_toggle"}
                id={selectedApp.id}
                type={"subs"}
                isSomethingChanged={isSomethingChanged}
                setIsSomethingChanged={setIsSomethingChanged}
                setSelectedApp={setSelectedApp}
              />
            </div>
            <div className={styles.headingRightContainer}>
              {canUpdateSubscription && (
                <button onClick={() => setShowUpdateDrawer(true)}>
                  Update Subscription
                </button>
              )}
              <button
                onClick={() => setShowAddSavings(true)}
                style={{ marginLeft: 8 }}
              >
                Add Savings
              </button>
            </div>
          </div>

          {showStandaloneVendorEditor && (
            <VendorPriceEditor
              subscription={selectedApp}
              onSaved={(data) => {
                const nextUnits = data?.data?.unit_ids;
                if (Array.isArray(nextUnits)) {
                  setSelectedApp({ ...selectedApp, unit_ids: nextUnits });
                }
                setIsSomethingChanged(!isSomethingChanged);
              }}
            />
          )}

          <div className={styles.subscriptionContainer}>
            <div className={styles.tableContainer}>
              <div className={styles.tableLeftContainer}>
                <div className={styles.leftColoumn}>
                  <span className={styles.tableTitle}>Created</span>
                  <span className={styles.tableTitle}>Current Period</span>
                  <span className={styles.tableTitle}>Id</span>
                  {selectedApp.period_cap != null && (
                    <span className={styles.tableTitle}>Paid seats</span>
                  )}
                  <span className={styles.tableTitle}>Auto licence sync</span>
                </div>
                <div className={styles.rightColoumn}>
                  <span className={styles.tableDesc}>
                    {selectedApp.createdAt
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-")}
                    ,{" "}
                    {selectedApp.createdAt
                      .split("T")[1]
                      .split(":")
                      .slice(0, 2)
                      .join(":")}
                  </span>
                  <span className={styles.tableDesc}>
                    {selectedApp.createdAt
                      .split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-")}{" "}
                    To{" "}
                    {selectedApp.dueDate
                      ?.split("T")[0]
                      .split("-")
                      .reverse()
                      .join("-")}
                  </span>
                  <span className={styles.tableDesc}>
                    {selectedApp.stripe_sub}
                  </span>
                  {/* Seat level already paid for this interval. Licences move
                      freely below it at no cost; only above it is charged. */}
                  {selectedApp.period_cap != null && (
                    <span className={styles.tableDesc}>
                      {selectedApp.period_cap}
                      {Number(selectedApp.noOfLicences) !==
                        Number(selectedApp.period_cap) &&
                        ` · ${selectedApp.noOfLicences} in use`}
                    </span>
                  )}
                  {/* Off means the 2-hourly sweep ignores this subscription.
                      Needed wherever the vendor's user count and the paid
                      licence count legitimately differ — every non-Flex
                      reseller plan — since the sweep would otherwise read the
                      gap as an error and order it away. */}
                  <span className={styles.tableDesc}>
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      {/* ToggleSwitch is purpose-built for the online-payment
                          flag (own fetch, hardcoded label), so this reuses its
                          styles rather than bending the component. */}
                      <button
                        type="button"
                        onClick={() => toggleAutoLicenseSync(!autoSyncEnabled)}
                        disabled={autoSyncSaving}
                        aria-pressed={autoSyncEnabled}
                        aria-label="Automatic licence sync"
                        className={`${toggleStyles.toggle} ${
                          autoSyncEnabled ? toggleStyles.enabled : toggleStyles.disabled
                        }`}
                      >
                        <span
                          className={`${toggleStyles.thumb} ${
                            autoSyncEnabled ? toggleStyles.thumbEnabled : ""
                          }`}
                        />
                      </button>
                      <span style={{ color: autoSyncEnabled ? "#1f2c3c" : "#b42318" }}>
                        {autoSyncSaving
                          ? "Saving…"
                          : autoSyncEnabled
                            ? "On — orders placed automatically"
                            : "Off — no automatic orders"}
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* REQUIRED FIELD HEADING */}
          <div className={styles.requiredHeadingContainer}>
            <h3>Required Fields</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />

          {/* REQUIRED FIELD CONTAINER */}
          <div className={styles.requiredFieldContainer}>
            <div className={styles.tableContainer}>
              <div className={styles.tableLeftContainer}>
                {selectedApp &&
                  (selectedApp.required_feilds?.length ?? 0) !== 0 &&
                  selectedApp.required_feilds.map((item, i) => (
                    <>
                      <div className={styles.leftColoumn}>
                        <span className={styles.tableTitle}>{item.title}</span>
                      </div>
                      <div className={styles.rightColoumn}>
                        <span className={styles.tableDesc}>{item.value}</span>
                      </div>
                    </>
                  ))}
                {selectedApp && (selectedApp.required_feilds?.length ?? 0) === 0 && (
                  <p>No data</p>
                )}
              </div>
            </div>
          </div>
          <div className={styles.upComingInvoiceHeadingContainer}>
            <h3>UpComing Invoice</h3>
            <p>
              This is a preview of the invoice that will be billed on{" "}
              <span style={{ fontWeight: "bold" }}>
                {selectedApp.dueDate
                  ?.split("T")[0]
                  .split("-")
                  .reverse()
                  .join("-")}
              </span>
              . It may Change if the subscription is updated.
            </p>
          </div>
          <div className={styles.horizontalLine} />

          <div className={styles.table}>
            <div className={styles.row}>
              <div className={styles.cell}>Description</div>
              <div className={styles.cell}>Qty</div>
              <div className={styles.cell}>Unit Price</div>
              <div className={styles.cell}>Amount</div>
            </div>
            {selectedApp?.unit_ids?.map((item, i) => (
              <div className={styles.row} key={i}>
                <div className={styles.cell}>{selectedApp.marketplaceName}</div>
                <div className={styles.cell}>{item.quantity}</div>
                <div className={styles.cell}>
                  {selectedApp.currency === "INR" ? "₹" : "$"}
                  {item.marketplace_price}
                </div>
                <div className={styles.cell}>
                  {selectedApp.currency === "INR" ? "₹" : "$"}
                  {item.quantity * item.marketplace_price}
                </div>
              </div>
            ))}

            {selectedApp.coupon && (
              <div className={styles.row}>
                <div className={styles.cell}></div>
                <div className={styles.cell}></div>
                <div
                  className={styles.cell}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Applied Coupon ({selectedApp.coupon.percent_off}% Off)
                </div>
              </div>
            )}

            {Array.isArray(selectedApp.itemCoupons) &&
              selectedApp.itemCoupons.map((ic, ici) => (
                <div className={styles.row} key={`ic-${ici}`}>
                  <div className={styles.cell}></div>
                  <div className={styles.cell}></div>
                  <div
                    className={styles.cell}
                    style={{ color: "#393939", fontWeight: "550" }}
                  >
                    Item coupon: {ic.name}
                    {ic.percentOff != null
                      ? ` (${ic.percentOff}% off)`
                      : ic.amountOff != null
                        ? ` (${ic.amountOff} off)`
                        : ""}
                    {ic.quantity != null
                      ? ` — on ${ic.quantity} license${ic.quantity === 1 ? "" : "s"}`
                      : ""}
                  </div>
                </div>
              ))}

            <div className={styles.row}>
              <div className={styles.cell}></div>
              <div className={styles.cell}></div>
              <div
                className={styles.cell}
                style={{ color: "#393939", fontWeight: "550" }}
              >
                Total Excluding Tax
              </div>
              <div
                className={styles.cell}
                style={{ color: "#393939", fontWeight: "550" }}
              >
                {selectedApp?.currency === "USD" ? "$" : "₹"}
                {selectedApp.amount}
              </div>
            </div>
            {isIndiaSubscription && (
              <div className={styles.row}>
                <div className={styles.cell}></div>
                <div className={styles.cell}></div>
                <div
                  className={styles.cell}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Tax (GST {previewTaxPercent}%)
                </div>
                <div
                  className={styles.cell}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  ₹{previewTaxAmount}
                </div>
              </div>
            )}
            {isIndiaSubscription && (
              <div className={styles.row}>
                <div className={styles.cell}></div>
                <div className={styles.cell}></div>
                <div
                  className={styles.cell}
                  style={{ color: "#393939", fontWeight: "700" }}
                >
                  Total Including Tax
                </div>
                <div
                  className={styles.cell}
                  style={{ color: "#393939", fontWeight: "700" }}
                >
                  ₹{previewTotalIncludingTax}
                </div>
              </div>
            )}
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Stripe Invoices</h3>
          </div>
          <div className={styles.horizontalLine} />

          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div className={styles.cell}>Amount</div>
                <div className={styles.cell}>Link</div>
                <div className={styles.cell}>Status</div>
                <div className={styles.cell}>Created</div>
                <div className={styles.cell}>Action</div>
              </div>
              {pastStripeInvoice &&
                pastStripeInvoice.map((item, i) => (
                  <div className={styles.row} key={i}>
                    <div className={styles.cell}>
                      {item.currency.toLowerCase() === "inr" ? "₹" : "$"}
                      {item.total / 100}
                    </div>
                    <div className={styles.cell}>
                      <a
                        href={item.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkIcon}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                    <div className={styles.cell}>{item.status}</div>
                    <div className={styles.cell}>
                      {new Date(item.created * 1000)
                        .toISOString()
                        .split("T")[0]
                        .split("-")
                        .reverse()
                        .join("-")}
                    </div>
                    <div className={styles.cell}>
                      {(item.status === "open" ||
                        item.status === "overdue") && (
                        <button
                          onClick={() => {
                            setPayId({
                              invoice_id: item.id,
                              region: selectedApp.regionId,
                            });
                            setPayConfirmation(true);
                          }}
                        >
                          Pay
                        </button>
                      )}
                      {/* Void is allowed while the Stripe invoice is not
                          in a terminal state. Hide entirely when the row
                          has no Stripe invoice ID — there's nothing to
                          void on Stripe's side. */}
                      {item.id &&
                        item.status !== "paid" &&
                        item.status !== "void" &&
                        item.status !== "uncollectible" && (
                          <button
                            style={{
                              marginLeft: 8,
                              background: "#fbe5e0",
                              color: "#b3261e",
                              border: "1px solid #f3b8ab",
                              padding: "4px 10px",
                              borderRadius: 6,
                              cursor: "pointer",
                              fontSize: 12,
                              fontWeight: 600,
                            }}
                            onClick={() => {
                              setVoidId({
                                invoice_id: item.id,
                                region: selectedApp.regionId,
                              });
                              setVoidConfirmation(true);
                            }}
                          >
                            Mark as void
                          </button>
                        )}
                      {/* Customer's manual payment awaiting verification —
                          Accept (mark paid out-of-band) or Reject (back to
                          overdue). Shown beside Pay / Mark-as-void. */}
                      {item._bill &&
                        item._bill.manual_status === "pending_verification" && (
                          <div
                            style={{
                              marginTop: 8,
                              padding: 8,
                              background: "#fff7ed",
                              border: "1px solid #fed7aa",
                              borderRadius: 6,
                              fontSize: 12,
                            }}
                          >
                            <div
                              style={{
                                fontWeight: 600,
                                color: "#9a3412",
                                marginBottom: 4,
                              }}
                            >
                              Manual payment claimed
                            </div>
                            <div style={{ color: "#7c2d12" }}>
                              UTR / Ref:{" "}
                              <b>{item._bill.manual_ref || "—"}</b>
                            </div>
                            {item._bill.manual_note && (
                              <div style={{ color: "#7c2d12" }}>
                                Note: {item._bill.manual_note}
                              </div>
                            )}
                            {item._bill.manual_screenshot && (
                              <a
                                href={item._bill.manual_screenshot}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  color: "#1c9aea",
                                  textDecoration: "underline",
                                }}
                              >
                                View screenshot
                              </a>
                            )}
                            <div
                              style={{
                                marginTop: 6,
                                display: "flex",
                                gap: 8,
                              }}
                            >
                              <button
                                onClick={() => acceptManualPayment(item)}
                                style={{
                                  background: "#dcfce7",
                                  color: "#166534",
                                  border: "1px solid #86efac",
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              >
                                Accept payment
                              </button>
                              <button
                                onClick={() => rejectManualPayment(item)}
                                style={{
                                  background: "#fbe5e0",
                                  color: "#b3261e",
                                  border: "1px solid #f3b8ab",
                                  padding: "4px 10px",
                                  borderRadius: 6,
                                  cursor: "pointer",
                                  fontSize: 12,
                                  fontWeight: 600,
                                }}
                              >
                                Reject payment
                              </button>
                            </div>
                          </div>
                        )}
                      {item.status === "paid" && "-"}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          {/* ONLY ORDER HEADING */}
          <div className={styles.priceHeadingContainer}>
            <h3>Orders</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Name
                </div>

                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Licenses
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Change
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Total Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Type
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastOrders &&
                pastOrders.map((item) => (
                  <>
                    <div className={styles.row}>
                      <div className={styles.cell}>Order_{item.id}</div>
                      <div className={styles.cell}>{item.marketplaceName}</div>
                      <div className={styles.cell}>{item.status}</div>
                      <div className={styles.cell}>{item.quantity}</div>
                      <div className={styles.cell}>
                        {/* Real movement. `diff` is the BILLABLE slice and is 0
                            for a below-cap change, so it is only a fallback for
                            orders predating license_delta. */}
                        {(() => {
                          const d =
                            item.license_delta ??
                            (item.type === "Remove"
                              ? -Math.abs(item.diff ?? 0)
                              : item.diff ?? 0);
                          if (!d) return "—";
                          return (
                            <span
                              style={{ color: d > 0 ? "#047857" : "#b45309" }}
                            >
                              {d > 0 ? `+${d}` : d}
                            </span>
                          );
                        })()}
                      </div>
                      <div className={styles.cell}>
                        {Number(item.subtotal_default) > 0
                          ? item.subtotal_default
                          : "No charge"}
                      </div>
                      <div className={styles.cell}>{item.type}</div>
                      <div className={styles.cell}>
                        {convertDateFromTFormat(item.createdAt)}
                      </div>
                    </div>
                  </>
                ))}
            </div>
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Bills</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Bill Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Books Sales Order
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Type
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Last Payment
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastBills &&
                pastBills?.map((item) => (
                  <div className={styles.row}>
                    <div className={styles.cell}>Bill_{item.id}</div>
                    <div className={styles.cell}>
                      {item.order_id ? `Order_${item.order_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.sales_order_id ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          <div style={{ fontWeight: 600 }}>
                            {item.sales_order_number || "—"}
                          </div>
                          <a
                            href={`https://books.zoho.in/app/60023272431#/salesorders/${item.sales_order_id}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: 12, color: "#6b7280" }}
                          >
                            {item.sales_order_id}
                          </a>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              type="button"
                              onClick={() =>
                                openAdminSalesOrder(
                                  item.sales_order_id,
                                  "inline",
                                  item.sales_order_number,
                                  showSalesOrderError,
                                )
                              }
                              style={{
                                border: "1px solid #d1d5db",
                                background: "#fff",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                openAdminSalesOrder(
                                  item.sales_order_id,
                                  "attachment",
                                  item.sales_order_number,
                                  showSalesOrderError,
                                )
                              }
                              style={{
                                border: "none",
                                background: "#2563eb",
                                color: "#fff",
                                borderRadius: 6,
                                padding: "2px 8px",
                                fontSize: 12,
                                cursor: "pointer",
                              }}
                            >
                              Download
                            </button>
                          </div>
                        </div>
                      ) : (
                        "-"
                      )}
                    </div>
                    <div className={styles.cell}>
                      {item.currency === "INR" ? "₹" : "$"}
                      {item.subtotal_default}
                    </div>
                    <div className={styles.cell}>{item.type}</div>
                    <div className={styles.cell}>{item.status}</div>
                    <div className={styles.cell}>{item.latestPayment}</div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(item.createdAt)}{" "}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Payments</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Payment Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Bill Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Link
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Status
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastPayments &&
                pastPayments?.map((item) => (
                  <div className={styles.row}>
                    <div className={styles.cell}>Payment_{item.id}</div>
                    <div className={styles.cell}>
                      {item.order_id ? `Order_${item.order_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.bill_id ? `Bill_${item.bill_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.currency === "INR" ? "₹" : "$"}
                      {item.amount}
                    </div>
                    <div className={styles.cell}>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkIcon}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                    <div className={styles.cell}>
                      {item.status === "failed" ? (
                        <div style={{ display: "flex", gap: 2 }}>
                          {item.status}
                          <InfoWithPopover reason={item.reason} />
                        </div>
                      ) : (
                        item.status
                      )}
                    </div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(item.createdAt)}{" "}
                    </div>
                  </div>
                ))}
            </div>
          </div>

          <div className={styles.priceHeadingContainer}>
            <h3>Invoices</h3>
          </div>
          {/* HORIZONTAL LINE */}
          <div className={styles.horizontalLine} />
          <div className={styles.invoiceContainer}>
            <div className={styles.table}>
              <div className={styles.row}>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Invoice Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Invoice Number
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Order Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Bill Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Payment Id
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Amount
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Link
                </div>
                <div
                  className={`${styles.cell} ${styles.title}`}
                  style={{ color: "#393939", fontWeight: "550" }}
                >
                  Date Created
                </div>
              </div>
              {pastInvoices &&
                pastInvoices?.map((item) => (
                  <div className={styles.row}>
                    <div className={styles.cell}>Invoice_{item.id}</div>
                    <div className={styles.cell}>{item.invoice_no}</div>
                    <div className={styles.cell}>
                      {item.order_id ? `Order_${item.order_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.bill_id ? `Bill_${item.bill_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.payment_id ? `Payment_${item.payment_id}` : "-"}
                    </div>
                    <div className={styles.cell}>
                      {item.currency === "INR" ? "₹" : "$"}
                      {item.subtotal}
                    </div>
                    <div className={styles.cell}>
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.linkIcon}
                      >
                        <FaExternalLinkAlt />
                      </a>
                    </div>
                    <div className={styles.cell}>
                      {convertDateFromTFormat(item.createdAt)}{" "}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}
      <Snackbar
        open={errorSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleErrorSnackbarClose}
      >
        <Alert
          onClose={handleErrorSnackbarClose}
          variant="filled"
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <AdminUpdateSubscriptionDrawer
        open={showUpdateDrawer}
        onClose={() => setShowUpdateDrawer(false)}
        subscription={selectedApp}
        onUpdated={handleSubscriptionUpdated}
      />
      <AddSavingsModal
        open={showAddSavings}
        onClose={() => setShowAddSavings(false)}
        subscription={selectedApp}
      />
    </>
  );
};

export default MyAppDetail;
